import React from "react";
import { motion } from "framer-motion";
import { Zap, ShieldCheck, Tag, Headphones, Package } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const ICONS = [Zap, ShieldCheck, Tag, Headphones];

export default function WhyChooseUs() {
  const { t } = useLanguage();

  return (
    <section
      id="why"
      data-testid="why-choose-us-section"
      className="bg-[#fafafa] py-24 md:py-32 dark:bg-[#080808]"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="grid items-start gap-10 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <span className="mb-4 inline-block text-xs font-semibold uppercase tracking-[0.3em] text-[#F37021]">
              — {t.why.eyebrow}
            </span>
            <h2 className="mb-5 font-display text-4xl font-bold leading-[1.08] text-[#111] sm:text-5xl md:text-6xl dark:text-white">
              {t.why.title}
            </h2>
            <p className="mb-8 text-base leading-relaxed text-[#555] md:text-lg dark:text-[#BAC4D4]">
              {t.why.subtitle}
            </p>

            {/* Decorative card */}
            <div className="relative mt-10 hidden lg:block">
              <div className="relative overflow-hidden rounded-[30px] border border-[#F37021]/15 bg-gradient-to-br from-[#FDF1E8] via-white to-[#FDF1E8] p-8 shadow-[0_22px_60px_rgba(16,24,40,0.08)] dark:border-white/10 dark:bg-[linear-gradient(135deg,#101010_0%,#181818_100%)]">
                <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-[#F37021]/15 blur-3xl" />
                <Package className="mb-4 h-10 w-10 text-[#F37021]" strokeWidth={1.5} />
                <p className="font-display text-2xl font-semibold leading-snug text-[#111] dark:text-white">
                  "We handle every parcel like it's our own."
                </p>
                <p className="mt-3 text-sm text-[#555] dark:text-[#BAC4D4]">- The BOB Team</p>
              </div>
            </div>
          </div>

          <div className="grid gap-5 sm:grid-cols-2 md:gap-6 lg:col-span-7">
            {t.why.items.map((item, i) => {
              const Icon = ICONS[i];
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="group relative rounded-[28px] border border-black/6 bg-white p-7 transition-all hover:-translate-y-1 hover:border-[#F37021]/28 hover:shadow-[0_24px_60px_rgba(16,24,40,0.08)] dark:border-white/10 dark:bg-[#121212] dark:hover:bg-[#1B1B1B]"
                  data-testid={`why-card-${i}`}
                >
                  <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#FDF1E8] transition-colors group-hover:bg-[#F37021]">
                    <Icon className="h-5 w-5 text-[#F37021] transition-colors group-hover:text-white" strokeWidth={1.8} />
                  </div>
                  <h3 className="mb-2 font-display text-xl font-semibold text-[#111] dark:text-white">
                    {item.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-[#555] dark:text-[#BAC4D4]">{item.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
